import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import type { Json } from "../integrations/supabase/database.types";
import { sourcedKey, useReadSource, viewRows } from "./readSource";
import { serviceEndedAt, type PortalService } from "./requestQueries";

/**
 * The client's own read of the account and access map, plan.md section 5.9.
 *
 * One read, `public.v_client_account_map`, plus the tenant's contacts so an owner can be named
 * rather than printed as a uuid. Nothing here is a gateway and nothing here writes: every row in
 * this register is written from `/admin/clients` through
 * `admin_upsert_account_reference`, `admin_create_access_record` and `admin_record_access_event`,
 * and `authenticated` holds no insert, update or delete grant on any of the three tables.
 *
 * ---------------------------------------------------------------------------
 * What this read does not contain, and why that is not a bug to fix here
 * ---------------------------------------------------------------------------
 *
 * plan.md section 5.9 lists "Which personal Petrovic identity has access" among the things the
 * register records, and then says "The client can see the ownership and access map. Admin-only
 * operational notes remain hidden." schema.md section 7 is the later and more specific authority
 * and it resolves that tension by removing Petrovic's own access rows from the client's read
 * entirely, behind two independent locks:
 *
 *   * the RLS policies `account_access_records_select_member` and
 *     `account_access_events_select_member`, both carrying `person_kind <> 'petrovic'`;
 *   * the view's own predicate, which excludes the same rows for every caller including one that
 *     no policy filters at all. `tests/db/access-map-read-model.test.ts` proves that second lock
 *     alone by reading the view as a role with `rolbypassrls`.
 *
 * So nothing in this file may filter, join or label its way back to a Petrovic identity, and
 * nothing in it should try to reconstruct one. The reason schema.md gives is worth repeating where
 * a future reader will be tempted: `provider_identity_snapshot` is clear text, and a personal
 * identity of Petrovic's published into a client's browser is the failure the second lock exists
 * for.
 *
 * The consequence is that an account whose only access row is Petrovic's arrives here with an empty
 * `access` array, which schema.md calls "the honest answer". `AccountsRoute` renders that as an
 * absence of *client-visible* access and says so in words, because an unlabelled empty list would
 * be read as "nobody can reach this account", and that is a different claim.
 *
 * Two more fields from section 5.9 are absent by the same design and are named here so a reader
 * does not go looking for a column that was never granted:
 *
 *   * "Notes required for handover or offboarding" reach the client only as
 *     `client_visible_note`. schema.md section 7: "Admin-only operational notes about an account
 *     are audit events or offboarding-task notes, not a client-visible column hidden by the
 *     interface."
 *   * `evidence_note` on an access event is not projected by the view at all, so the history a
 *     client reads carries what happened and when, and not what somebody wrote about it.
 *
 * ---------------------------------------------------------------------------
 * `active` is carried and never filtered
 * ---------------------------------------------------------------------------
 *
 * The view projects `active` without filtering it, and this file keeps that decision rather than
 * quietly reinstating the filter in the browser. schema.md section 7 states the reason: `active =
 * false` is a fact about an account's own life, and dropping the row would take a closed account's
 * revocation history out of the client's read at the exact moment they have a reason to look for
 * it. Ordering puts the open accounts first; nothing is removed.
 */

export const clientAccountMapKey = (tenantId: string) =>
  ["portal", "account-map", tenantId] as const;
export const tenantContactsKey = (tenantId: string) =>
  ["portal", "account-map-contacts", tenantId] as const;

/**
 * One thing that happened to one access, as the view nests it under the access it belongs to.
 *
 * `account_access_record_id` is declared here even though the view can only ever return a non-null
 * one: an event whose record was deleted has a null there, nothing left to join to, and leaves the
 * client's read through the view's inner join. It is carried so the type matches the column the
 * migration projects rather than the subset this caller happens to receive.
 *
 * `provider_role_before` and `provider_role_after` are independently nullable. A `granted` event has
 * an after and no before; a `revoked` event has neither.
 */
export interface ClientAccessEvent {
  id: string;
  tenant_id: string;
  account_access_record_id: string | null;
  provider_snapshot: string;
  provider_identity_snapshot: string;
  /** One of `requested`, `granted`, `role_changed`, `reviewed`, `missing` and `revoked`. */
  event_type: string;
  provider_role_before: string | null;
  provider_role_after: string | null;
  occurred_at: string;
}

/**
 * One person who can reach one account, with that access's own history under it.
 *
 * `person_kind` is never `petrovic` in this read. It is one of `client`, `successor` and `other`,
 * and the screen names all three, because a client reading who can reach their own supplier account
 * needs to see a predecessor agency that still holds a login as plainly as they see themselves.
 *
 * `revoked_at` is set exactly when `access_status` is `revoked`, which
 * `account_access_records_revoked_consistency_check` guarantees. `last_reviewed_at` is independent:
 * an access nobody has reviewed has no review date, and that is a finding rather than a gap to hide.
 */
export interface ClientAccessRecord {
  id: string;
  tenant_id: string;
  account_reference_id: string;
  /** `client`, `successor` or `other`. Never `petrovic`; see the file comment. */
  person_kind: string;
  provider_identity_label: string;
  provider_role: string;
  /** One of `requested`, `active`, `missing` and `revoked`. */
  access_status: string;
  granted_at: string | null;
  last_reviewed_at: string | null;
  revoked_at: string | null;
  events: ClientAccessEvent[];
}

/** One row of `public.v_client_account_map`, exactly the columns the view projects. */
export interface ClientAccountReference {
  id: string;
  tenant_id: string;
  service_id: string | null;
  provider: string;
  account_label: string;
  service_url: string | null;
  non_secret_identifier: string | null;
  client_owner_contact_id: string;
  recovery_owner_contact_id: string | null;
  /** One of `client_owned`, `petrovic_held_pending_transfer` and `third_party_owned`. */
  ownership_status: string;
  /** One of `enabled`, `disabled`, `unknown` and `not_applicable`. */
  mfa_status: string;
  client_visible_note: string | null;
  active: boolean;
  /**
   * Null only when the `jsonb` was not an array at all, which no version of the view produces.
   *
   * It is a declared state rather than a silent empty list because the two are opposite claims. An
   * empty array says no client-visible access is recorded against this account, which is something
   * to act on. A shape this build cannot read says nothing at all, and rendering the second as the
   * first is the same defect as rendering a failed read as a calm fact. `app/data/adminClientQueries.ts`
   * makes the identical distinction on the admin side.
   */
  access: ClientAccessRecord[] | null;
}

/**
 * The nested array as a list, or null when what arrived was not one.
 *
 * The generated type declares `access` as `Json`, a union that includes a string, a number and
 * null, so TypeScript refuses to read it as a list until somebody decides. Deciding once, here,
 * is what stops each consumer from casting on its own and being wrong in a different way.
 *
 * The inner cast is unchecked on purpose, for the reason `toAccessRecords` in
 * `app/data/adminClientQueries.ts` gives: validating every field of every entry would be a second
 * hand-written description of a shape the migration already states. The one distinction worth
 * making is a list from something that is not a list.
 */
function toAccessRecords(value: Json): ClientAccessRecord[] | null {
  if (!Array.isArray(value)) {
    return null;
  }

  return (value as unknown as ClientAccessRecord[]).map((record) => ({
    ...record,
    // The view coalesces both levels to `[]`, so this is belt and braces rather than a fix. It is
    // here because one non-array nested inside an otherwise good answer would otherwise crash the
    // render of every other account on the page, and an account map that fails to draw because one
    // access has no history is worse than one that draws the history as empty.
    events: Array.isArray(record?.events) ? record.events : []
  }));
}

/**
 * Every supplier account this tenant depends on, with the access the client may see under each.
 *
 * No `tenant_id` filter and no `order by`. RLS returns exactly this session's tenant, the arrays
 * inside each row are ordered by the view's own `order by` clauses, and the ordering *between* rows
 * is decided in `composeAccountMap` rather than by the server, because it depends on the contact
 * names this query does not carry.
 *
 * The column list is written out rather than starred, and it is a literal in the call rather than a
 * constant above it, which is what `statusQueries.ts` and `analyticsQueries.ts` both do. Two
 * reasons, and they point the same way. The view is `security_invoker` and projects only columns
 * migration 4 already granted on the three base tables, so a select naming anything outside that
 * grant is refused for the whole row and the list is part of the read rather than tidiness. And
 * supabase-js infers the row type from the select string as a literal, so a constant spliced in
 * would give this query a generic row and quietly move the checking of every field below into the
 * cast at the end.
 */
export function useClientAccountMap(
  tenantId: string | null
): UseQueryResult<ClientAccountReference[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, clientAccountMapKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<Omit<ClientAccountReference, "access"> & { access: Json }>(
          "admin_as_client_account_map",
          { p_tenant_id: source.tenantId }
        );
        return rows.map((row) => ({ ...row, access: toAccessRecords(row.access) }));
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("v_client_account_map")
        .select(
          "id, tenant_id, service_id, provider, account_label, service_url, non_secret_identifier, client_owner_contact_id, recovery_owner_contact_id, ownership_status, mfa_status, client_visible_note, active, access"
        );

      if (error) {
        throw error;
      }

      return (data ?? []).map((row) => ({
        ...row,
        access: toAccessRecords(row.access)
      })) as ClientAccountReference[];
    }
  });
}

export interface TenantContact {
  id: string;
  full_name: string;
}

/**
 * The tenant's own people, so `client_owner_contact_id` can be rendered as a person.
 *
 * Its own read rather than `app/data/tenantRoster.ts`, which answers a different question:
 * that module joins `memberships` to `contacts` and returns a map from `user_id` to a name, because
 * what it is for is naming the author of a message. An account owner is a `contact_id` and may be
 * somebody with no portal login at all, so the roster's map would silently drop exactly the owners
 * this screen most needs to name.
 *
 * `contacts_select_member` scopes to `public.current_tenant_ids()` and migration
 * `20260829120002_identity_tenants_memberships` grants `authenticated` both columns below. The
 * explicit `tenant_id` filter is still sent, for the reason `tenantRoster.ts` gives: a person can
 * hold memberships in more than one tenant and the policy would return all of them.
 *
 * A failed read throws rather than resolving to an empty list. An empty contact list renders every
 * account as having no named owner, which is the finding this register exists to surface, and a
 * network fault must never be able to manufacture it.
 */
export function useTenantContacts(tenantId: string | null): UseQueryResult<TenantContact[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, tenantContactsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<TenantContact>("admin_as_client_contacts", { p_tenant_id: source.tenantId });
        return rows.map(({ id, full_name }) => ({ id, full_name }));
      }
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("contacts")
        .select("id, full_name")
        .eq("tenant_id", tenantId as string);

      if (error) {
        throw error;
      }
      return (data ?? []) as TenantContact[];
    }
  });
}

export interface AccountMapEntry {
  reference: ClientAccountReference;
  /** Null when `public.services` has no row this membership may read, or the account names none. */
  serviceName: string | null;
  /** Build round 46, 2026-10-17: when the account's service was archived, so the card says it ended. */
  serviceEndedAt: string | null;
  /**
   * Null when the contacts read returned nobody with that id.
   *
   * `account_references.client_owner_contact_id` is `not null` behind a restricting foreign key and
   * `contacts.full_name` is `not null` too, so the owner exists by construction and this null means
   * the contact row was not in the answer rather than that the account has no owner. The screen
   * says which of those it is instead of printing a blank.
   */
  clientOwnerName: string | null;
  /**
   * The real optional one of the pair.
   *
   * `recovery_owner_contact_id` is nullable in the table, and an account with nobody named to
   * recover it is an account nobody has thought through. The screen says so rather than leaving the
   * row short, which is the same call `/admin/clients` makes about the same column.
   */
  recoveryOwnerName: string | null;
  /** True when the reference names no recovery owner at all, as opposed to naming one we cannot find. */
  recoveryOwnerAbsent: boolean;
}

/**
 * The accounts in reading order, with the ids resolved to names.
 *
 * Open accounts first, then the closed ones, each group by provider and then by the account's own
 * label. The sort is here rather than in the query because the register is a handful of rows per
 * tenant and because `active` is the split that matters to a reader: a closed account is history
 * and belongs under the accounts that are still running, not interleaved with them.
 *
 * `localeCompare` with `sv` so å, ä and ö sort where a Swedish reader expects them.
 */
export function composeAccountMap(
  references: ClientAccountReference[],
  contacts: TenantContact[],
  services: PortalService[]
): AccountMapEntry[] {
  const contactName = new Map(contacts.map((contact) => [contact.id, contact.full_name]));
  const serviceName = new Map(services.map((service) => [service.id, service.display_name]));
  const serviceEnded = new Map(services.map((service) => [service.id, serviceEndedAt(service)]));

  return references
    .map((reference) => ({
      reference,
      serviceName: reference.service_id ? serviceName.get(reference.service_id) ?? null : null,
      serviceEndedAt: reference.service_id ? serviceEnded.get(reference.service_id) ?? null : null,
      clientOwnerName: contactName.get(reference.client_owner_contact_id) ?? null,
      recoveryOwnerName: reference.recovery_owner_contact_id
        ? contactName.get(reference.recovery_owner_contact_id) ?? null
        : null,
      recoveryOwnerAbsent: reference.recovery_owner_contact_id === null
    }))
    .sort((first, second) => {
      if (first.reference.active !== second.reference.active) {
        return first.reference.active ? -1 : 1;
      }

      const byProvider = first.reference.provider.localeCompare(second.reference.provider, "sv");
      return byProvider !== 0
        ? byProvider
        : first.reference.account_label.localeCompare(second.reference.account_label, "sv");
    });
}
